/**
 * Candidate job alerts client (Bet 1).
 *
 * Contract: `@vhire/shared/service-types/job-alerts`.
 * Plan: docs/plans/JOB_ALERTS_AND_ENGAGEMENT_PLAN_2026-09-28.md §5.
 *
 * Two families of routes:
 *
 *  - CANDIDATE routes (`/candidates/me/job-alerts…`) carry the candidate bearer
 *    token from `getAuthToken()` (`vhire_token`) — never the admin token
 *    (kb: frontend--admin-vs-candidate-auth-tokens). A 401 throws AUTH_REQUIRED
 *    and the caller decides what to show; this module never redirects.
 *  - PUBLIC routes (`/v1/job-alerts/…`) carry no token. The unsubscribe token
 *    in the email link is the whole credential, and attribution beacons fire
 *    whether or not anyone is signed in.
 *
 * NOTE: API_BASE already ends in `/api`, so every path here starts AFTER that
 * prefix. `/api/candidates/...` would build `/api/api/...` and 404.
 *
 * Errors are one typed class, `JobAlertsApiError(code, status)`. The contract's
 * codes pass through untouched — in particular JOB_ALERTS_UNAVAILABLE, the
 * signal the UI uses to hide the whole feature quietly while migration 268 is
 * not applied.
 */

import {
  JOB_ALERT_CONSENT_COPY_VERSION,
  JOB_ALERT_UNSUBSCRIBE_TOKEN_RE,
  type JobAlert,
  type JobAlertCriteria,
  type JobAlertEngagementKind,
  type JobAlertErrorCode,
  type JobAlertFrequency,
  type JobAlertSource,
  type ListJobAlertsResult,
  type UnsubscribeJobAlertsResult,
} from '@vhire/shared/service-types/job-alerts';
import { API_BASE } from '@/lib/config';
import { getAuthToken } from '@/lib/api';

/** Contract codes plus the transport outcomes only a client can observe. */
export type JobAlertsClientErrorCode =
  | JobAlertErrorCode
  | 'AUTH_REQUIRED'
  | 'RATE_LIMITED'
  | 'NETWORK'
  | 'REQUEST_FAILED';

export class JobAlertsApiError extends Error {
  readonly code: JobAlertsClientErrorCode;
  readonly status: number;
  constructor(code: JobAlertsClientErrorCode, status: number, message: string) {
    super(message);
    this.name = 'JobAlertsApiError';
    this.code = code;
    this.status = status;
  }
}

/**
 * Every server code the contract defines. A Record, so a new JobAlertErrorCode
 * fails the build here until it is listed — an unlisted code would otherwise
 * degrade to REQUEST_FAILED and, for JOB_ALERTS_UNAVAILABLE, show a broken
 * feature instead of hiding it.
 */
const SERVER_CODES: Record<JobAlertErrorCode, true> = {
  INVALID_REQUEST: true,
  NOT_FOUND: true,
  ALERT_LIMIT_REACHED: true,
  DUPLICATE_ALERT: true,
  CONSENT_REQUIRED: true,
  NO_EMAIL: true,
  CANDIDATE_INACTIVE: true,
  JOB_ALERTS_UNAVAILABLE: true,
  INTERNAL_ERROR: true,
};

function isServerCode(code: unknown): code is JobAlertErrorCode {
  return typeof code === 'string' && Object.prototype.hasOwnProperty.call(SERVER_CODES, code);
}

type Envelope = {
  success?: unknown;
  data?: unknown;
  error?: { code?: unknown; message?: unknown } | string;
} | null;

async function readEnvelope(res: Response): Promise<Envelope> {
  try {
    return (await res.json()) as Envelope;
  } catch {
    return null;
  }
}

function errorFrom(status: number, payload: Envelope): JobAlertsApiError {
  if (status === 401) return new JobAlertsApiError('AUTH_REQUIRED', 401, 'Your session expired. Sign in again.');
  if (status === 429) return new JobAlertsApiError('RATE_LIMITED', 429, 'Too many attempts. Wait a minute and try again.');
  const error = payload?.error ?? null;
  const code = typeof error === 'object' ? error?.code : undefined;
  const serverMessage = typeof error === 'object' ? error?.message : error;
  const message = typeof serverMessage === 'string' && serverMessage ? serverMessage : 'Request failed';
  return new JobAlertsApiError(isServerCode(code) ? code : 'REQUEST_FAILED', status, message);
}

type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE';

async function send(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, { cache: 'no-store', ...init });
  } catch {
    throw new JobAlertsApiError('NETWORK', 0, 'Could not reach VHire. Check your connection and try again.');
  }
}

async function candidateFetch<T>(path: string, method: Method, body?: unknown): Promise<T> {
  const token = getAuthToken();
  if (!token) {
    throw new JobAlertsApiError('AUTH_REQUIRED', 401, 'Sign in to manage job alerts.');
  }
  const res = await send(`${API_BASE}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const payload = await readEnvelope(res);
  if (res.ok && payload?.success === true) {
    return payload.data as T;
  }
  throw errorFrom(res.status, payload);
}

const ALERTS_PATH = '/candidates/me/job-alerts';
const alertPath = (id: string) => `${ALERTS_PATH}/${encodeURIComponent(id)}`;

/** The candidate's alerts, their digest address, the cap, and a profile-based suggestion. */
export function listJobAlerts(): Promise<ListJobAlertsResult> {
  return candidateFetch<ListJobAlertsResult>(ALERTS_PATH, 'GET');
}

export interface CreateJobAlertInput {
  name?: string;
  criteria: JobAlertCriteria;
  frequency: JobAlertFrequency;
  source: JobAlertSource;
  /**
   * The candidate ticked the consent box. Typed as the literal `true` so no
   * call site can compile without deciding it — the server refuses anything
   * else with CONSENT_REQUIRED.
   */
  consentAccepted: true;
}

/**
 * Creates an alert. The consent copy version is the contract constant, so the
 * alert row records exactly the sentence the dialog showed.
 */
export function createJobAlert(input: CreateJobAlertInput): Promise<{ alert: JobAlert }> {
  return candidateFetch<{ alert: JobAlert }>(ALERTS_PATH, 'POST', {
    ...(input.name !== undefined ? { name: input.name } : {}),
    criteria: input.criteria,
    frequency: input.frequency,
    source: input.source,
    consentAccepted: input.consentAccepted,
    consentCopyVersion: JOB_ALERT_CONSENT_COPY_VERSION,
  });
}

export interface UpdateJobAlertInput {
  name?: string;
  frequency?: JobAlertFrequency;
  /** `active` re-activates (counts against the cap); `paused` pauses with reason `candidate`. */
  status?: 'active' | 'paused';
  /**
   * The candidate ticked the consent box again. Required by the server when
   * `status: 'active'` turns an UNSUBSCRIBED alert back on; the copy version
   * sent with it is the contract constant, as on create.
   */
  consentAccepted?: true;
}

export function updateJobAlert(id: string, patch: UpdateJobAlertInput): Promise<{ alert: JobAlert }> {
  const body = patch.consentAccepted ? { ...patch, consentCopyVersion: JOB_ALERT_CONSENT_COPY_VERSION } : patch;
  return candidateFetch<{ alert: JobAlert }>(alertPath(id), 'PATCH', body);
}

export function deleteJobAlert(id: string): Promise<{ deleted: true }> {
  return candidateFetch<{ deleted: true }>(alertPath(id), 'DELETE');
}

/**
 * `/alerts/unsubscribe/<token>` — stops every alert on the address the email
 * went to. The body is exactly `{}`: the path carries the token, and a JSON
 * body is what tells this page's POST apart from a mail client's RFC 8058
 * one-click POST to the same route.
 *
 * "Done" is never a guess: only a 2xx that confirms the result shape resolves.
 * A 4xx other than 429 is a refusal of this link (terminal); anything else is
 * ours to fix and safe to retry — unsubscribing is idempotent.
 *
 * A token that fails the contract's format check never leaves the browser, so
 * nothing but a 43-character base64url value is ever spliced into the path.
 */
export async function unsubscribeJobAlertsByToken(token: string): Promise<UnsubscribeJobAlertsResult> {
  if (!JOB_ALERT_UNSUBSCRIBE_TOKEN_RE.test(token)) {
    throw new JobAlertsApiError('NOT_FOUND', 404, 'This unsubscribe link is not valid.');
  }
  const res = await send(`${API_BASE}/v1/job-alerts/unsubscribe/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
  const payload = await readEnvelope(res);
  const data = payload?.data as Partial<UnsubscribeJobAlertsResult> | null | undefined;
  if (
    res.ok &&
    payload?.success === true &&
    typeof data?.unsubscribed === 'number' &&
    typeof data.alreadyUnsubscribed === 'boolean'
  ) {
    return { unsubscribed: data.unsubscribed, alreadyUnsubscribed: data.alreadyUnsubscribed };
  }
  if (res.status === 429) {
    throw new JobAlertsApiError('RATE_LIMITED', 429, 'Too many attempts. Wait a minute and try again.');
  }
  if (res.status >= 400 && res.status < 500) {
    throw new JobAlertsApiError('NOT_FOUND', res.status, 'This unsubscribe link is not valid.');
  }
  throw new JobAlertsApiError('REQUEST_FAILED', res.status, 'Unsubscribing is briefly unavailable. Try again shortly.');
}

/**
 * Click/apply attribution for a digest (`ja=<runId>` links). Fire-and-forget:
 * NEVER throws, never awaited, and `keepalive` lets the request outlive a
 * navigation. The route answers 202 whatever happens, so there is nothing to
 * read back.
 */
export function recordJobAlertEngagement(input: {
  runId: string;
  jobSlug: string;
  kind: JobAlertEngagementKind;
}): void {
  try {
    void fetch(`${API_BASE}/v1/job-alerts/engagements`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ runId: input.runId, jobSlug: input.jobSlug, kind: input.kind }),
      keepalive: true,
      cache: 'no-store',
    }).catch(() => {});
  } catch {
    /* fetch unavailable — attribution is best-effort */
  }
}
